import { useEffect, useState } from 'react';
import { PaginationControls, SearchBar, Tabs, TabsType } from '../../../../../components';
import { KNOWLEDGE_HUB_TYPE_CONFIG } from '../../../../../constants';
import { useAppSelector, useHandleApiResponse } from '../../../../../hooks';
import { KnowledgeHubRecord, useGetKnowledgeHubMutation } from '../../../../../services';
import { KnowledgeHubRecordType } from '../../../../../types';
import { getKnowledgeHubIcon, kebabCaseToLabel } from '../../../../../utils';
import ScenarioPanelContent from './ScenarioPanelContent';

const SCENARIO_PANEL_PAGE_SIZE = 15;

const ScenarioPanel = () => {
  const [activeTab, setActiveTab] = useState(KnowledgeHubRecordType.PRIORITY);
  // Local state for managing existing records fetched from the server.
  const [records, setRecords] = useState<KnowledgeHubRecord[]>([]);
  // Local state for managing pagination.
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [search, setSearch] = useState('');

  const orgId = useAppSelector((state) => state.prospect.orgId);

  const [getKnowledgeHub, { isLoading }] = useGetKnowledgeHubMutation();

  const handleApiResponse = useHandleApiResponse();

  // Reset state when the active tab changes.
  useEffect(() => {
    setRecords([]);
    setTotalPages(0);
    setPage(0);
    setSearch('');
  }, [activeTab]);

  useEffect(() => {
    const fetchKnowledgeHub = async () => {
      const response = await getKnowledgeHub({
        orgId,
        page: page + 1,
        perPage: SCENARIO_PANEL_PAGE_SIZE,
        search,
        type: activeTab,
      });

      handleApiResponse({
        response,
        hideErrorToast: true,
        hideSuccessToast: true,
        onSuccess: (data) => {
          // Update state with fetched data.
          setRecords(data.records);
          setTotalPages(data.pagination.totalPages);
        },
      });
    };

    fetchKnowledgeHub();
  }, [orgId, page, search, activeTab, getKnowledgeHub, handleApiResponse]);

  return (
    <>
      <SearchBar
        value={search}
        onSubmit={(value) => {
          // Reset the page to the first page to avoid empty results if the user is on a higher page and the new search query yields fewer total pages.
          setPage(0);
          setSearch(value);
        }}
      />
      <div className="flex h-full flex-col overflow-hidden">
        <Tabs
          tabs={Object.values(KnowledgeHubRecordType).map((type) => {
            const activeColor = KNOWLEDGE_HUB_TYPE_CONFIG[type].color;
            const icon = getKnowledgeHubIcon(type);
            const title = kebabCaseToLabel(KNOWLEDGE_HUB_TYPE_CONFIG[type].path);
            return {
              activeColor,
              content: (
                <ScenarioPanelContent hasSearch={!!search} isLoading={isLoading} records={records} type={type} />
              ),
              icon,
              id: type,
              title,
            };
          })}
          activeTab={activeTab}
          type={TabsType.BORDERED}
          setActiveTab={(newActiveTab) => setActiveTab(newActiveTab as KnowledgeHubRecordType)}
        />
        {totalPages > 1 && <PaginationControls currentPage={page} totalPages={totalPages} setCurrentPage={setPage} />}
      </div>
    </>
  );
};

export default ScenarioPanel;
